import {
  EVENT_NAME,
  addId,
  config,
  getIds,
  getStorage,
  hasId,
  removeId,
  setStorage,
  syncToApi,
} from './favorites-storage.js';
import { replaceContainerHtml } from '@theme/dom-utils';
import { bindUI } from '@theme/cart-ui';

const CART_WISHLIST_TEXT_ADD = 'Move to Favorites';
const CART_WISHLIST_TEXT_ACTIVE = 'In Favorites';

function createHeartImg(src) {
  const img = document.createElement('img');
  img.src = src || '';
  img.alt = '';
  img.width = 20;
  img.height = 20;
  return img;
}

function isPlaceholderImageSrc(src) {
  if (!src || typeof src !== 'string') return true;
  return src.startsWith('data:');
}

function resolveImageFromSrcset(srcset) {
  if (!srcset || typeof srcset !== 'string') return '';
  const first = srcset.split(',')[0]?.trim()?.split(/\s+/)[0];
  return first || '';
}

function resolveProductImageFromRoot(root) {
  if (!root) return '';

  const fromDataAttr = (el) => el?.getAttribute?.('data-product-image')?.trim() || '';
  let image =
    fromDataAttr(root) ||
    fromDataAttr(root.closest?.('[data-product-image]')) ||
    fromDataAttr(root.querySelector?.('[data-product-image]')) ||
    '';

  if (image) return image;

  const cardLink = root.closest?.('product-card-link, [data-featured-media-url]') || root.querySelector?.('product-card-link, [data-featured-media-url]');
  image = cardLink?.getAttribute('data-featured-media-url')?.trim() || '';
  if (image) return image;

  const imgs = root.querySelectorAll?.(
    '.cart__card-img, .product-media__image, .product-media img, .card-gallery img, .product-card__image img, .resource-card__media img, img[src*="cdn.shopify"], img[src*="shopifycdn"]'
  );
  if (!imgs?.length) return '';

  for (const img of imgs) {
    const src = img.currentSrc || img.src || '';
    if (!isPlaceholderImageSrc(src)) return src;
    const fromSet = resolveImageFromSrcset(img.getAttribute('srcset'));
    if (fromSet && !isPlaceholderImageSrc(fromSet)) return fromSet;
  }

  return '';
}

function resolveProductTitleFromRoot(root) {
  if (!root) return '';

  let title = root.getAttribute?.('data-product-title')?.trim() || '';
  if (!title) {
    title = root.querySelector?.('[data-product-title]')?.getAttribute('data-product-title')?.trim() || '';
  }
  if (!title) {
    const titleEl = root.querySelector?.(
      '.cart__card-name, .product-title-link, .product-card__title-text, .title-text, .product-card__title, h1.product-title-text, .product-title-text'
    );
    if (titleEl) title = titleEl.textContent.trim();
  }
  return title;
}

function createFavoriteButton(productId, heartUrl, heartActiveUrl, active = false, meta = null) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'product-card__favorites-btn';
  btn.dataset.favoritesToggle = '';
  btn.dataset.productId = String(productId || '');
  btn.setAttribute('aria-label', active ? 'Remove from favorites' : 'Add to favorites');
  if (meta?.title) btn.dataset.productTitle = meta.title;
  if (meta?.image) btn.dataset.productImage = meta.image;
  if (active) btn.dataset.active = 'true';

  const outline = document.createElement('span');
  outline.className = 'ic-heart ic-heart--outline';
  outline.appendChild(createHeartImg(heartUrl));

  const filled = document.createElement('span');
  filled.className = 'ic-heart ic-heart--active';
  filled.appendChild(createHeartImg(heartActiveUrl));

  btn.appendChild(outline);
  btn.appendChild(filled);
  return btn;
}

function createSkeletonItem() {
  const li = document.createElement('li');
  li.className = 'product-grid__item favorites-skeleton-item';
  ['favorites-skeleton__image', 'favorites-skeleton__title', 'favorites-skeleton__title favorites-skeleton__title--short', 'favorites-skeleton__price'].forEach((className) => {
    const block = document.createElement('div');
    block.className = `favorites-skeleton ${className}`;
    li.appendChild(block);
  });
  return li;
}

function createSkeletonGrid(count) {
  const wrapper = document.createElement('div');
  wrapper.className = 'grid main-collection-grid spacing-style';
  wrapper.style.gridColumn = '1 / -1';

  const inner = document.createElement('div');
  inner.style.gridColumn = 'var(--full-width)';
  inner.style.containerType = 'inline-size';
  inner.style.containerName = 'product-grid';

  const ul = document.createElement('ul');
  ul.className = 'product-grid product-grid--grid';
  ul.setAttribute('product-grid-view', 'default');
  ul.setAttribute('role', 'list');

  for (let i = 0; i < count; i += 1) {
    ul.appendChild(createSkeletonItem());
  }

  inner.appendChild(ul);
  wrapper.appendChild(inner);
  return wrapper;
}

function getToggleUrls() {
  const link = document.querySelector('[data-favorites-menu-item]');
  let outlineSrc = link ? link.getAttribute('data-heart-outline-src') : '';
  let activeSrc = link ? link.getAttribute('data-heart-active-src') : '';
  if (!outlineSrc || !activeSrc) {
    const tplData = document.getElementById('favorites-toggle-tpl-data');
    if (tplData) {
      try {
        const data = JSON.parse(tplData.textContent);
        if (data.heartUrl) outlineSrc = data.heartUrl;
        if (data.heartActiveUrl) activeSrc = data.heartActiveUrl;
      } catch (error) {
        console.warn('Failed to parse favorites toggle template data', error);
      }
    }
  }
  return { outlineSrc, activeSrc };
}

function syncButtons() {
  const list = getStorage();
  document.querySelectorAll('[data-favorites-toggle]').forEach((btn) => {
    const id = btn.getAttribute('data-product-id');
    const active = id && list.some((f) => String(f.id) === String(id));
    if (active) {
      btn.setAttribute('data-active', 'true');
      btn.setAttribute('aria-label', btn.hasAttribute('data-cart-move-to-favorites') ? CART_WISHLIST_TEXT_ADD : 'Remove from Favorites');
    } else {
      btn.removeAttribute('data-active');
      btn.setAttribute('aria-label', CART_WISHLIST_TEXT_ADD);
    }
    if (!btn.getAttribute('data-product-image')) {
      const image = resolveProductImageFromRoot(getCard(btn) || btn.closest('.cart__card, .cart-items__row, .cart-item, .shopify-section') || btn);
      if (image) btn.dataset.productImage = image;
    }
    if (!btn.getAttribute('data-product-title')) {
      const title = resolveProductTitleFromRoot(getCard(btn) || btn.closest('.cart__card, .cart-items__row, .cart-item, .shopify-section') || btn);
      if (title) btn.dataset.productTitle = title;
    }
    const textEl = btn.querySelector('.cart__btn-wishlist-text');
    if (textEl) textEl.textContent = btn.hasAttribute('data-cart-move-to-favorites') ? CART_WISHLIST_TEXT_ADD : (active ? CART_WISHLIST_TEXT_ACTIVE : CART_WISHLIST_TEXT_ADD);
  });
}

function updateFavoritesMenu() {
  const count = getStorage().length;
  let outlineSrc = null;
  let activeSrc = null;
  document.querySelectorAll('[data-favorites-menu-item]').forEach((link) => {
    if (outlineSrc === null) outlineSrc = link.getAttribute('data-heart-outline-src');
    if (activeSrc === null) activeSrc = link.getAttribute('data-heart-active-src');
    const img = link.querySelector('.ic-heart-icon img');
    const bubble = link.querySelector('[data-favorites-bubble]');
    if (img) img.src = count > 0 && activeSrc ? activeSrc : (outlineSrc || img.src);
    if (bubble) {
      bubble.textContent = count;
      bubble.style.display = count > 0 ? '' : 'none';
    }
  });
}

function bounceBtn(btn) {
  if (!btn) return;
  btn.classList.add('favorites-heart-bounce');
  setTimeout(() => btn.classList.remove('favorites-heart-bounce'), 400);
}

function getWishlistUrl() {
  return document.querySelector('[data-favorites-menu-item]')?.getAttribute('href') || '/pages/favorites';
}

function getProductMetaFromButton(btn) {
  let title = btn?.getAttribute('data-product-title')?.trim() || '';
  let image = btn?.getAttribute('data-product-image')?.trim() || '';

  const cartItem = btn?.closest('.cart__card');
  if (cartItem) {
    if (!title) title = resolveProductTitleFromRoot(cartItem);
    if (!image) image = resolveProductImageFromRoot(cartItem);
  }

  const productSection = btn?.closest('.product-information') || (!btn ? document.querySelector('.product-information') : null);

  if (productSection) {
    if (!title) title = resolveProductTitleFromRoot(productSection);
    if (!image) image = resolveProductImageFromRoot(productSection);
  }

  if (!title || !image) {
    const card = btn && !btn.closest('.product-information') ? getCard(btn) : null;
    if (card) {
      if (!title) title = resolveProductTitleFromRoot(card);
      if (!image) image = resolveProductImageFromRoot(card);
    }
  }

  return { title, image };
}

function getJsonRoute(route, fallback) {
  const value = route || fallback;
  return value.endsWith('.js') ? value : `${value}.js`;
}

function getCartSectionsToUpdate() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    const sectionId = component.getAttribute('data-section-id');
    if (sectionId) sections.add(sectionId);
  });
  return [...sections];
}

async function moveCartLineToFavorites(btn) {
  const line = Number(btn.getAttribute('data-cart-line') || btn.closest('.cart__card')?.getAttribute('data-cart-line') || 0);
  const key = btn.getAttribute('data-cart-item-key') || btn.closest('.cart__card')?.getAttribute('data-item-key') || '';
  if (!line && !key) return null;

  const sections = getCartSectionsToUpdate();
  const body = {
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };

  if (key) {
    body.id = key;
  } else {
    body.line = line;
  }

  btn.disabled = true;
  const response = await fetch(getJsonRoute(window.Theme?.routes?.cart_change_url, '/cart/change.js'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.errors) {
    throw new Error(data.errors || data.description || data.message || 'Failed to update cart');
  }

  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: {
      cart: data,
      item_count: data.item_count || 0,
      data: {
        itemCount: data.item_count || 0,
        source: 'favorites-move-to-favorites',
        sections: data.sections || {},
      },
    },
  }));
  document.dispatchEvent(new CustomEvent('cart:updated', {
    bubbles: true,
    detail: {
      cart: data,
      item_count: data.item_count || 0,
      items: data.items || [],
    },
  }));
  document.dispatchEvent(new CustomEvent('shopify:cart:update', {
    bubbles: true,
    detail: {
      cart: data,
      item_count: data.item_count || 0,
    },
  }));

  return data;
}


function isFavoritesGridInSync(grid) {
  const ids = getIds();
  const ul = grid.querySelector('ul.product-grid');
  if (!ul) return ids.length === 0;
  if (ul.querySelector('.favorites-skeleton-item')) return false;

  const itemIds = [...ul.querySelectorAll('.product-grid__item[data-product-id]')].map((item) =>
    String(item.getAttribute('data-product-id'))
  );

  if (itemIds.length !== ids.length) return false;

  const idSet = new Set(ids.map(String));
  return itemIds.every((id) => idSet.has(id));
}

function restoreFavorite(productId) {
  if (!productId || hasId(productId)) return;
  addId(productId);
  syncToApi();
  syncButtons();
  updateFavoritesMenu();
  updateFavoritesPageCount();

  const emptyEl = document.querySelector('[data-favorites-empty]');
  if (emptyEl) emptyEl.classList.add('hidden');
  updateHeaderDescription(false);
  loadFavoritesPage();
}

function showToast({ added, productId, triggerBtn, meta: presetMeta }) {
  if (typeof window.showSystemToast !== 'function') return;

  const meta = presetMeta || (triggerBtn ? getProductMetaFromButton(triggerBtn) : getProductMetaFromButton(null));
  const wishlistUrl = getWishlistUrl();

  if (added) {
    window.showSystemToast({
      title: 'Added to wishlist',
      subtitle: meta.title || 'You can find it in your wishlist.',
      image: meta.image,
      action: {
        label: 'View wishlist',
        href: wishlistUrl,
      },
      duration: 4500,
    });
    return;
  }

  window.showSystemToast({
    title: 'Removed from wishlist',
    subtitle: meta.title || 'The item has been removed from your wishlist.',
    image: meta.image,
    action: {
      label: 'Undo',
      onClick: () => {
        restoreFavorite(productId);
      },
    },
    duration: 5000,
  });
}

function getCard(btn) {
  if (btn?.closest('.product-information')) return null;
  const card =
    btn.closest('.product-grid__item, .grid__item, .related-product-card') ||
    btn.closest('product-card-link, product-card, .resource-card');
  if (card) return card;
  let el = btn.parentElement;
  const grid = document.querySelector('[data-favorites-grid]');
  while (el && el !== grid) {
    if (el.querySelector?.('a[href*="/products/"]')) return el;
    el = el.parentElement;
  }
  return null;
}

function getProductId(btn) {
  const card = getCard(btn);
  if (card) {
    const id = card.getAttribute('data-product-id');
    if (id) return id;
    const inner = card.querySelector('[data-product-id]');
    if (inner) return inner.getAttribute('data-product-id');
  }
  return btn.getAttribute('data-product-id') || null;
}

function getHeaderDescriptionEl() {
  const grid = document.querySelector('[data-favorites-grid]');
  if (!grid) return null;
  const sectionId = grid.getAttribute('data-section-id');
  if (sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      const el = section.querySelector('.cs-header__description');
      if (el) return el;
    }
  }
  const main = document.getElementById('MainContent');
  return main ? main.querySelector('.cs-header__description') : document.querySelector('.cs-header__description');
}

function updateHeaderDescription(showEmpty) {
  const grid = document.querySelector('[data-favorites-grid]');
  const descEl = getHeaderDescriptionEl();
  if (!grid || !descEl) return;
  const emptyText = grid.getAttribute('data-favorites-empty-text') || '';
  if (showEmpty) {
    if (!descEl.dataset.originalDescription) descEl.dataset.originalDescription = descEl.textContent || '';
    descEl.textContent = emptyText;
  } else if (descEl.dataset.originalDescription) {
    descEl.textContent = descEl.dataset.originalDescription;
    delete descEl.dataset.originalDescription;
  }
}

function updateFavoritesPageCount() {
  const section = document.querySelector('.main-favorites-section');
  if (!section) return;
  const countEl = document.getElementById('favorites-count') || section.querySelector('.cs-header__count');
  if (!countEl) return;
  const n = getStorage().length;
  countEl.textContent = n > 0 ? String(n) : '';
}

function reorderByIds(grid, ids) {
  const ul = grid.querySelector('ul.product-grid');
  if (!ul || !ids.length) return;
  const byId = {};
  ul.querySelectorAll('.product-grid__item').forEach((item) => {
    const id = item.getAttribute('data-product-id');
    if (id) byId[id] = item;
  });
  ids.forEach((id) => {
    const item = byId[String(id)];
    if (item) ul.appendChild(item);
  });
}

function injectButtons(grid) {
  if (!grid.hasAttribute('data-favorites-grid')) return;
  const tplEl = document.getElementById('favorites-toggle-tpl-data');
  if (!tplEl?.textContent) return;
  let urls;
  try {
    urls = JSON.parse(tplEl.textContent);
  } catch (error) {
    console.warn('Failed to parse favorites template URLs', error);
    return;
  }

  const heart = urls.heartUrl || '';
  const heartAct = urls.heartActiveUrl || '';
  const isFavoritesGrid = grid.hasAttribute('data-favorites-grid');

  grid.querySelectorAll('.product-grid__item').forEach((card) => {
    const meta = {
      title: resolveProductTitleFromRoot(card),
      image: resolveProductImageFromRoot(card),
    };

    const existingBtn = card.querySelector('[data-favorites-toggle]');
    if (existingBtn) {
      if (meta.image && !existingBtn.getAttribute('data-product-image')) {
        existingBtn.dataset.productImage = meta.image;
      }
      if (meta.title && !existingBtn.getAttribute('data-product-title')) {
        existingBtn.dataset.productTitle = meta.title;
      }
      return;
    }

    const pid = card.getAttribute('data-product-id') || card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
    if (!pid) return;

    const imgContainer = card.querySelector('.product-card__image-container') || card.querySelector('.card-gallery') || card.querySelector('.resource-card__media');
    const imgLink = card.querySelector('a.product-card__link, a[href*="/products/"]');

    if ((imgContainer || imgLink) && (isFavoritesGrid || imgContainer)) {
      if (imgLink) imgLink.classList.add('product-card__link--with-favorites');
      const container = imgContainer || imgLink?.querySelector('.product-card__image-container') || imgLink;
      if (container) {
        container.appendChild(createFavoriteButton(pid, heart, heartAct, isFavoritesGrid, meta));
      }
      return;
    }

    const titleEl = card.querySelector('.product-card__title') || card.querySelector('[data-block-type="product-title"]') || card.querySelector('a.product-title-link:not(.product-card__link)') || card.querySelector('.product-title-link');
    if (titleEl) {
      if (imgLink) imgLink.classList.add('product-card__link--with-favorites');
      const wrapper = document.createElement('div');
      wrapper.className = 'product-card__title-row';
      titleEl.parentNode.insertBefore(wrapper, titleEl);
      wrapper.appendChild(titleEl);
      wrapper.appendChild(createFavoriteButton(pid, heart, heartAct, isFavoritesGrid, meta));
    } else if (imgLink) {
      imgLink.classList.add('product-card__link--with-favorites');
      imgLink.insertAdjacentElement('afterend', createFavoriteButton(pid, heart, heartAct, isFavoritesGrid, meta));
    }
  });
}

function loadFavoritesPage() {
  const grid = document.querySelector('[data-favorites-grid]');
  const emptyEl = document.querySelector('[data-favorites-empty]');
  const loaderEl = document.querySelector('[data-favorites-loader]');
  if (!grid || !emptyEl) return;

  if (isFavoritesGridInSync(grid)) {
    syncButtons();
    if (loaderEl) loaderEl.classList.add('hidden');
    updateHeaderDescription(false);
    updateFavoritesPageCount();
    return;
  }

  const ids = getIds();
  if (ids.length === 0) {
    if (loaderEl) loaderEl.classList.add('hidden');
    grid.replaceChildren();
    emptyEl.classList.remove('hidden');
    updateFavoritesPageCount();
    return;
  }

  const query = ids.map((id) => `id:${id}`).join(' OR ');
  if (loaderEl) loaderEl.classList.add('hidden');
  grid.replaceChildren();
  grid.appendChild(createSkeletonGrid(Math.min(ids.length, 10)));

  const searchUrl = new URL(grid.getAttribute('data-favorites-search-url') || '/search', window.location.origin);
  searchUrl.searchParams.set('type', 'product');
  searchUrl.searchParams.set('q', query);

  fetch(searchUrl.toString(), { cache: 'no-store' })
    .then((r) => r.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const listEl = doc.querySelector('#ResultsList ul.product-grid') || doc.querySelector('ul.product-grid');
      if (!listEl?.innerHTML?.trim()) {
        grid.replaceChildren();
        emptyEl.classList.remove('hidden');
        updateFavoritesPageCount();
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'grid main-collection-grid spacing-style';
      wrapper.style.gridColumn = '1 / -1';

      const inner = document.createElement('div');
      inner.style.gridColumn = 'var(--full-width)';
      inner.style.containerType = 'inline-size';
      inner.style.containerName = 'product-grid';
      inner.appendChild(listEl.cloneNode(true));
      wrapper.appendChild(inner);

      replaceContainerHtml(grid, wrapper.outerHTML);
      requestAnimationFrame(() => {
        injectButtons(grid);
        reorderByIds(grid, ids);
        bindUI(grid);
        syncButtons();
        updateHeaderDescription(false);
        updateFavoritesPageCount();
        document.dispatchEvent(new CustomEvent('theme:convert-prices'));
      });
      emptyEl.classList.add('hidden');
    })
    .catch((error) => {
      console.warn('Failed to load favorites page products', error);
      grid.replaceChildren();
      emptyEl.classList.remove('hidden');
    });
}

function bootFavorites() {
  const c = config();
  if (c.customerLoggedIn && Array.isArray(c.initialFavorites) && c.initialFavorites.length > 0) {
    const list = c.initialFavorites.map((item) => {
      const id = (item && item.id != null) ? Number(item.id) : Number(item);
      return (id === id) ? { id } : null;
    }).filter(Boolean);
    if (list.length > 0) setStorage(list);
  }
  if (c.customerLoggedIn && c.favoritesApiUrl && c.customerId) syncToApi();
  syncButtons();
  updateFavoritesMenu();
  updateFavoritesPageCount();
  loadFavoritesPage();
}

document.addEventListener(EVENT_NAME, () => {
  updateFavoritesMenu();
  updateFavoritesPageCount();
});

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-favorites-toggle]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  const productId = getProductId(btn);
  if (!productId) return;

  const toastMeta = getProductMetaFromButton(btn);
  const moveFromCart = btn.hasAttribute('data-cart-move-to-favorites');
  const grid = document.querySelector('[data-favorites-grid]');
  const onFavoritesPage = grid && btn.closest('[data-favorites-grid]');
  let added = false;

  if (moveFromCart) {
    if (!hasId(productId)) addId(productId);
    added = true;
  } else if (onFavoritesPage) {
    const removed = removeId(productId);
    if (removed) {
      getCard(btn)?.remove();
    }
  } else if (hasId(productId)) {
    removeId(productId);
    added = false;
  } else {
    addId(productId);
    added = true;
  }

  syncToApi();
  syncButtons();
  bounceBtn(btn);
  showToast({ added, productId, triggerBtn: btn, meta: toastMeta });

  if (moveFromCart) {
    moveCartLineToFavorites(btn).catch((error) => {
      console.error('Failed to move cart item to favorites', error);
      btn.disabled = false;
    });
  }

  if (onFavoritesPage && getIds().length === 0) {
    const g = document.querySelector('[data-favorites-grid]');
    const emptyEl = document.querySelector('[data-favorites-empty]');
    if (g) g.replaceChildren();
    if (emptyEl) emptyEl.classList.remove('hidden');
  }
}, true);

window.favorites = {
  get: getStorage,
  getIds,
  has: hasId,
  add: addId,
  remove: removeId,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootFavorites);
} else {
  bootFavorites();
}
